import * as React from 'react';
import { ApplicationState, AppThunkDispatch, AppThunkAction } from '../../store';
import { connect } from 'react-redux';
import LoginForm, { LoginModalStateProps, LoginModalDispatchProps, LoginModalOwnProps } from './Login';
import { loadBaseData, loggedIn, login, loginWithGoogle, verifyMfa } from '../../store/session/actions';
import { alertSuccess, alertError } from '../../store/alerts/actions';
import { TK } from '../../store/translations/translationKeys';
import { sendResetPasswordEmailAsync, signUpAsync, SignUpRequest } from '../../ajax/requests';
import { getErrorMessage } from '../../ajax/errors';
import { translate, getLanguage } from '../../store/translations/hooks';
import { LoggedUser } from '../../models';
import { saveAuthContext } from '../../shared/authContext';
import { OnboardingData } from '../../components/ProfessionalOnboardingWizard/ProfessionalOnboardingWizard';
import { getDetectedCountryCode } from '../../shared/countryCode';

type OnboardingAuthData = { googleToken?: string; username?: string; password?: string };

const resetPassword = (username: string, isClient: boolean): AppThunkAction<Promise<void>> => {
    return async (dispatch, getState) => {
        try {
            await sendResetPasswordEmailAsync({username, isClient});
            dispatch(alertSuccess(translate(TK.resetPasswordEmailSent)));
        } catch {
            dispatch(alertError(translate(TK.cantSendResetPasswordEmail)));
        }
    }
}

const handleGoogleSignIn = (idToken: string, isClient: boolean, companyId?: number): AppThunkAction<Promise<LoggedUser | null>> => {
    return async (dispatch, getState) => {
        return dispatch(loginWithGoogle(idToken, isClient, companyId));
    }
}

export const buildSignUpRequestFromOnboarding = (
    data: OnboardingData,
    auth: OnboardingAuthData,
    consentCulture: string,
    countryCode: string,
): SignUpRequest => {
    const parsePrice = (value?: string) => {
        if (!value) return undefined;
        const numeric = Number(value);
        return Number.isFinite(numeric) && numeric >= 0 ? numeric : undefined;
    };

    const services = data.services
        .filter(service => service.name && service.name.trim().length > 0)
        .map(service => ({
            name: service.name.trim(),
            defaultPrice: parsePrice(service.price),
        }));

    const rooms = (data.rooms || []).filter(room => room && room.trim().length > 0).map(room => room.trim());

    const professionals = (data.professionals || [])
        .filter(professional => (professional.name && professional.name.trim().length > 0) || (professional.email && professional.email.trim().length > 0))
        .map(professional => ({
            name: professional.name?.trim() || professional.email?.trim(),
            email: professional.email?.trim(),
        }));

    const request: SignUpRequest = {
        company: data.companyName?.trim(),
        servicesWithPrice: services,
        rooms,
        professionals,
        notifyProfessionalsByEmail: data.notifyProfessionalsByEmail,
        acceptedTermsAndConditions: data.acceptedTermsAndConditions,
        acceptedPrivacyPolicy: data.acceptedPrivacyPolicy,
        marketingAcceptedByEmail: data.marketingAcceptedByEmail,
        consentCulture,
        countryCode,
        partnerCode: data.partnerCode || undefined,
        partnerId: data.partnerId || undefined,
    };

    if (auth.googleToken) {
        request.googleAuth = { idToken: auth.googleToken };
    } else if (data.adminCredentials) {
        request.username = data.adminCredentials.email;
        request.password = data.adminCredentials.password;
    }

    return request;
}

export const handleOnboardingComplete = (
    data: OnboardingData,
    auth: OnboardingAuthData
): AppThunkAction<Promise<'loggedIn' | null>> => {
    return async (dispatch, getState) => {
        try {
            // For non-Google sign-up, credentials come from OnboardingData
            if (!auth.googleToken && !data.adminCredentials) {
                dispatch(alertError(translate(TK.signUpEmailPasswordRequired)));
                return null;
            }

            const request = buildSignUpRequestFromOnboarding(
                data,
                auth,
                getLanguage(),
                getDetectedCountryCode(),
            );

            const user = await signUpAsync(request);

            // Both Google and username/password sign-up now return LoggedUser
            // Strip token before storing in Redux (auth is cookie-based)
            const { token, ...userWithoutToken } = user;
            saveAuthContext('professional');
            dispatch(loggedIn(userWithoutToken as LoggedUser));
            dispatch(loadBaseData());
            dispatch(alertSuccess(translate(TK.loggedIn)));
            return 'loggedIn';
        } catch (error) {
            dispatch(alertError(getErrorMessage(error) ?? translate(TK.failedToCompleteOnboarding)));
            return null;
        }
    }
}

const mapStateToProps = (state: ApplicationState): LoginModalStateProps => ({
    isLoggingIn: state.session.isLoggingIn,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch): LoginModalDispatchProps => ({
    onSubmit: (username, password, isClient, companyId, impersonateUsername) => dispatch(login(username, password, isClient, companyId, impersonateUsername)),
    onResetPassword: (username, isClient) => dispatch(resetPassword(username, isClient)),
    onGoogleSignIn: (idToken, isClient, companyId) => dispatch(handleGoogleSignIn(idToken, isClient, companyId)),
    onCompleteOnboarding: (data, auth) => dispatch(handleOnboardingComplete(data, auth)),
    onVerifyMfa: (sessionId, otp, isClient, mfaMethod) => dispatch(verifyMfa(sessionId, otp, isClient, mfaMethod)),
    googleClientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
});

const ConnectedLoginForm = connect<LoginModalStateProps, LoginModalDispatchProps, LoginModalOwnProps, ApplicationState>(
    mapStateToProps,
    mapDispatchToProps,
)(LoginForm as any);

// Export separate components for professional and client login
export const LoginProfessional = () => <ConnectedLoginForm context="professional" />;
export const LoginClient = () => <ConnectedLoginForm context="client" />;

// Default export - deprecated, use LoginProfessional or LoginClient instead
export default ConnectedLoginForm;
